import { Injectable } from '@angular/core';
import { Observable, Subject, of, race, timer } from 'rxjs';
import { map, take } from 'rxjs/operators';

export interface NativeSession {
  currentUser?: any;
  deviceId?: string;
  platform?: string;

  pendingRoute?: string | null;
}

const CHANNEL = 'BaknaNative';

const SESSION_TIMEOUT_MS = 4000;

const WEB_DEVICE_ID_KEY = 'bakna.deviceId';

@Injectable({ providedIn: 'root' })
export class NativeBridgeService {
  private sessionSubject = new Subject<NativeSession>();
  private deviceIdFromNative: string | null = null;
  private platformFromNative: string | null = null;

  constructor() {
    (window as any).__baknaSession = (payload: string | NativeSession) => {
      const session = this.parse(payload);
      if (session.deviceId) this.deviceIdFromNative = session.deviceId;
      if (session.platform) this.platformFromNative = session.platform;
      this.sessionSubject.next(session);
    };
  }

  get isNative(): boolean {
    return !!(window as any)[CHANNEL];
  }

  get deviceId(): string {
    if (this.deviceIdFromNative) return this.deviceIdFromNative;

    let id = localStorage.getItem(WEB_DEVICE_ID_KEY);
    if (!id) {
      id = this.randomId();
      localStorage.setItem(WEB_DEVICE_ID_KEY, id);
    }
    return id;
  }

  get platform(): string {
    return this.platformFromNative || 'web';
  }

  install(onNavigate: (route: string) => void): void {
    (window as any).__baknaNavigate = (route: string) => {
      if (typeof route === 'string' && route.length) onNavigate(route);
    };
  }

  requestSession(): Observable<NativeSession> {
    if (!this.isNative) return of({});

    this.post({ type: 'session:request' });

    return race(
      this.sessionSubject.pipe(take(1)),
      timer(SESSION_TIMEOUT_MS).pipe(map(() => ({} as NativeSession))),
    ).pipe(take(1));
  }

  saveSession(currentUser: any): void {
    this.post({ type: 'session:save', currentUser });
  }

  clearSession(): void {
    this.post({ type: 'session:clear' });
  }

  private post(message: any): void {
    const channel = (window as any)[CHANNEL];
    if (!channel || typeof channel.postMessage !== 'function') return;

    try {
      channel.postMessage(JSON.stringify(message));
    } catch (e) {
      console.error('[Bakna] native bridge postMessage failed', e);
    }
  }

  private parse(payload: string | NativeSession): NativeSession {
    if (!payload) return {};
    if (typeof payload !== 'string') return payload;

    try {
      return JSON.parse(payload) || {};
    } catch (e) {
      console.error('[Bakna] native session payload inválido', e);
      return {};
    }
  }

  private randomId(): string {
    const crypto = window.crypto as any;
    if (crypto?.randomUUID) return crypto.randomUUID();

    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, (b: number) => b.toString(16).padStart(2, '0')).join('');
  }
}
